import { useQuery } from "@tanstack/react-query";

import { useFeature } from "@/app/session/features";
import { useCan } from "@/app/session/permissions";
import type { NavEntry } from "@/app/shell/nav";
import { myEmployeeKey } from "@/features/people/api";
import { api, unwrap } from "@/shared/api/client";

/* A record changes only when HR opens or closes it, so a page load reads it once. */
const RECORD_STALE_MS = 5 * 60 * 1000;

/**
 * Whether the signed-in person has an employee record (HR opened one for them). Asked only
 * when it decides something; a 404 means no record.
 */
function useHasEmployeeRecord(enabled: boolean): boolean {
  const query = useQuery({
    queryKey: myEmployeeKey,
    queryFn: () => unwrap(api.GET("/api/v1/me/employee")),
    enabled,
    retry: false,
    staleTime: RECORD_STALE_MS,
  });
  return query.isSuccess;
}

/**
 * Whether a main section shows for the signed-in person: its permission, its area switched on,
 * and for the employees' own pages (My HR, requests, company news) a record of their own, or
 * hr.view. Owners hold every key, so they always see every section; they decide who else does
 * by opening records and granting keys.
 */
export function useNavAllowed(entry: NavEntry): boolean {
  const permitted = useCan(entry.permission);
  const switchedOn = useFeature(entry.feature);
  const seesPeople = useCan("hr.view");
  const asks = Boolean(entry.forEmployees) && permitted && switchedOn && !seesPeople;
  const employee = useHasEmployeeRecord(asks);
  return permitted && switchedOn && (!entry.forEmployees || seesPeople || employee);
}
